/**
 * Better Auth Client for Vue.js
 * Works with Netlify Blobs-backed auth server
 */

const API_BASE = "/.netlify/functions/auth";

class BetterAuthClient {
  constructor() {
    this.sessionToken = null;
    this.user = null;
    this.listeners = [];
    
    // Load session from localStorage on init
    if (typeof window !== "undefined") {
      this.sessionToken = localStorage.getItem("better-auth.session_token");
      this.loadSession();
    }
  }

  /**
   * Add event listener
   */
  on(event, callback) {
    this.listeners.push({ event, callback });
  }

  /**
   * Emit event
   */
  emit(event, data) {
    this.listeners.forEach(({ event: e, callback }) => {
      if (e === event) callback(data);
    });
  }

  /**
   * Make API request
   */
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (this.sessionToken) {
      headers.Authorization = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Request failed");
    }

    return data;
  }

  /**
   * Sign in
   */
  async signIn(email, password) {
    const response = await this.request("/sign-in", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    this.sessionToken = response.data.session.token;
    this.user = response.data.user;

    // Store session token
    if (typeof window !== "undefined") {
      localStorage.setItem("better-auth.session_token", this.sessionToken);
    }

    this.emit("signIn", this.user);
    return { user: this.user, session: response.data.session };
  }

  /**
   * Sign up
   */
  async signUp(email, password, name) {
    const response = await this.request("/sign-up", {
      method: "POST",
      body: JSON.stringify({ email, password, name }),
    });

    this.sessionToken = response.data.session.token;
    this.user = response.data.user;

    if (typeof window !== "undefined") {
      localStorage.setItem("better-auth.session_token", this.sessionToken);
    }

    this.emit("signUp", this.user);
    return { user: this.user, session: response.data.session };
  }

  /**
   * Sign out
   */
  async signOut() {
    try {
      await this.request("/sign-out", {
        method: "POST",
      });

      this.sessionToken = null;
      this.user = null;

      if (typeof window !== "undefined") {
        localStorage.removeItem("better-auth.session_token");
      }

      this.emit("signOut", null);
      return true;
    } catch (error) {
      // Clear local state even if request fails
      this.sessionToken = null;
      this.user = null;
      if (typeof window !== "undefined") {
        localStorage.removeItem("better-auth.session_token");
      }
      throw error;
    }
  }

  /**
   * Get current session
   */
  async getSession() {
    try {
      const response = await this.request("/session");
      this.sessionToken = response.data.session.token;
      this.user = response.data.user;

      if (typeof window !== "undefined") {
        localStorage.setItem("better-auth.session_token", this.sessionToken);
      }

      return { user: this.user, session: response.data.session };
    } catch (error) {
      this.sessionToken = null;
      this.user = null;
      if (typeof window !== "undefined") {
        localStorage.removeItem("better-auth.session_token");
      }
      return null;
    }
  }

  /**
   * Load session (non-blocking)
   */
  async loadSession() {
    if (!this.sessionToken) return null;
    return this.getSession();
  }

  /**
   * Get current user
   */
  async getUser() {
    try {
      const response = await this.request("/user");
      this.user = response.data;
      return this.user;
    } catch (error) {
      this.user = null;
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    if (!this.sessionToken) {
      // Try to load from localStorage
      if (typeof window !== "undefined") {
        this.sessionToken = localStorage.getItem("better-auth.session_token");
      }
      if (!this.sessionToken) return false;
    }

    try {
      const session = await this.getSession();
      return !!session;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current user (synchronous, may be null)
   */
  getCurrentUser() {
    return this.user;
  }

  /**
   * Forgot password
   */
  async forgotPassword(email) {
    return await this.request("/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Reset password
   */
  async resetPassword(resetToken, newPassword) {
    return await this.request("/reset-password", {
      method: "POST",
      body: JSON.stringify({ resetToken, newPassword }),
    });
  }

  /**
   * List all users (admin only)
   */
  async listUsers() {
    const response = await this.request("/users");
    return response.data || [];
  }

  /**
   * Update user (admin only)
   */
  async updateUser(userId, updates) {
    return await this.request("/users", {
      method: "PUT",
      body: JSON.stringify({ userId, ...updates }),
    });
  }

  /**
   * Delete user (admin only)
   */
  async deleteUser(userId, email) {
    return await this.request("/users", {
      method: "DELETE",
      body: JSON.stringify({ userId, email }),
    });
  }

  /**
   * Change password (for logged-in users to change their own password)
   */
  async changePassword(currentPassword, newPassword) {
    return await this.request("/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }
}

// Export singleton instance
export const auth = new BetterAuthClient();

// Export class for testing
export default BetterAuthClient;

